import React from "react";
import { useRealtimeStatus } from "./RealtimeProvider";

const StatusDot = ({ color }: { color: string }) => (
  <span
    style={{
      display: "inline-block",
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      backgroundColor: color,
      marginRight: "8px",
      verticalAlign: "middle",
    }}
  />
);

export function RealtimeStatusIndicator({ className }: { className?: string }) {
  const { connectionState, subscribedTables } = useRealtimeStatus();

  // Hidden by default
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Alt+Enter toggles visibility
      if (e.altKey && e.key === "Enter") {
        setVisible((v) => !v);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const statusInfo = React.useMemo(() => {
    switch (connectionState) {
      case "CONNECTING":
        return { color: "#faad14", text: "Real-time: Connecting..." };
      case "OPEN":
        return { color: "#52c41a", text: "Real-time: Connected" };
      case "CLOSING":
        return { color: "#faad14", text: "Real-time: Disconnecting..." };
      case "CLOSED":
        return { color: "#ff4d4f", text: "Real-time: Disconnected" };
      default:
        return { color: "#8c8c8c", text: "Real-time: Unknown" };
    }
  }, [connectionState]);

  return (
    <div
      className={className}
      style={{
        display: visible ? "block" : "none", // toggle with Alt+Enter
        fontFamily: "sans-serif",
        fontSize: "12px",
        padding: "8px",
        border: "1px solid #e8e8e8",
        borderRadius: "4px",
        backgroundColor: "#fafafa",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
        <StatusDot color={statusInfo.color} />
        {statusInfo.text}
      </div>
      <div>
        <strong>{subscribedTables.length}</strong> table(s) subscribed:
        {subscribedTables.length > 0 ? (
          <ul
            style={{
              margin: "4px 0 0 20px",
              padding: 0,
              fontSize: "11px",
              color: "#595959",
            }}
          >
            {subscribedTables.map((table) => (
              <li key={table}>{table}</li>
            ))}
          </ul>
        ) : (
          <p
            style={{
              margin: "4px 0 0 20px",
              fontStyle: "italic",
              color: "#8c8c8c",
            }}
          >
            No components are currently listening for changes.
          </p>
        )}
      </div>
    </div>
  );
}